import React from 'react';
import {
  Play, Award, Sparkles, Store, ChefHat, Refrigerator, Wind,
  WashingMachine, Snowflake, Utensils,
} from 'lucide-react';
import useReveal from '../hooks/useReveal';

const Spotlight = () => {
  useReveal();
  return (
    <section className="py-16 lg:py-24 bg-[#1a1410] text-[#F7F4EA] relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#F58220]/25 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#E9D6C1]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div className="reveal">
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#F58220] mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Store Spotlight
          </div>
          <h2 className="h-display uppercase text-[40px] md:text-[60px] leading-[1.02]">
            A reliable neighbourhood <span className="text-[#F58220]">destination</span>
          </h2>
          <p className="mt-6 text-[15px] leading-[1.85] text-[#F7F4EA]/75 max-w-[560px]">
            <span className="text-[#F7F4EA] font-semibold">Veetu Saama Home Appliance Shop</span>{' '}
            is a reliable neighborhood destination for all your home appliance
            needs. From refrigerators and washing machines to air conditioners,
            microwaves, and kitchen essentials, we offer a wide selection at
            competitive prices.
          </p>
          <p className="mt-4 text-[15px] leading-[1.85] text-[#F7F4EA]/75 max-w-[560px]">
            Featuring top-quality products from trusted brands like LG, Dyson,
            Hafele, Faber, Aquaguard, Samsung, IFB, Bosch and more.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="https://www.instagram.com/veetusaama/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#F58220] text-white hover:bg-[#C7580A] transition-colors duration-500 text-[11px] uppercase tracking-[0.24em] font-bold"
            >
              <div className="w-7 h-7 rounded-full bg-white text-[#F58220] flex items-center justify-center">
                <Play className="w-3 h-3 fill-current ml-0.5" />
              </div>
              Follow Us
            </a>
            <div className="flex items-center gap-3 px-5 py-3 rounded-full border border-[#F7F4EA]/20">
              <Award className="w-4 h-4 text-[#F58220]" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F7F4EA]/80">
                Authorised Dealer
              </span>
            </div>
          </div>
        </div>

        {/* Illustrated store panel */}
        <div className="reveal relative">
          <div
            className="relative rounded-3xl overflow-hidden aspect-video shadow-2xl border border-[#F7F4EA]/10"
            style={{
              background: 'linear-gradient(160deg, #F7F4EA 0%, #E9D6C1 100%)',
            }}
          >
            {/* Store facade illustration */}
            <div className="absolute inset-0 flex flex-col">
              {/* Roof */}
              <div className="h-[22%] bg-[#1a1410] relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-display uppercase text-[24px] md:text-[30px] tracking-widest">
                    <span className="text-[#F7F4EA]">Veetu </span>
                    <span className="text-[#F58220]">Saama</span>
                  </div>
                </div>
                <div className="absolute -bottom-3 left-0 right-0 h-6 bg-[#F58220]" />
              </div>
              {/* Window row */}
              <div className="flex-1 grid grid-cols-4 gap-3 p-6 pt-9">
                {[ChefHat, Refrigerator, Wind, WashingMachine].map((Icon, i) => (
                  <div
                    key={i}
                    className="rounded-lg bg-white flex items-center justify-center border-2 border-[#F58220]/20 hover:border-[#F58220] transition-colors duration-500 relative overflow-hidden group cursor-pointer"
                  >
                    <div className="absolute inset-x-0 top-0 h-6 bg-[#F58220]/20" />
                    <Icon className="w-8 h-8 md:w-10 md:h-10 text-[#4D4D4D] group-hover:text-[#F58220] transition-colors duration-500 relative" strokeWidth={1.4} />
                  </div>
                ))}
              </div>
              {/* Door base */}
              <div className="h-[12%] bg-[#4D4D4D] flex items-center justify-center">
                <div className="w-16 h-6 rounded-t-lg bg-[#1a1410] flex items-center justify-center">
                  <div className="w-1 h-3 bg-[#F58220] rounded-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Featured chip */}
          <div className="absolute -bottom-6 -right-6 bg-[#F58220] text-white rounded-2xl px-6 py-5 shadow-2xl hidden md:block">
            <div className="h-display text-[22px] leading-none">Adyar</div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/85 mt-2">
              Chennai Store
            </div>
          </div>
          {/* Store badge */}
          <div className="absolute -top-5 -left-5 bg-[#F7F4EA] text-[#1a1410] rounded-2xl px-5 py-3 shadow-2xl hidden md:flex items-center gap-2">
            <Store className="w-4 h-4 text-[#F58220]" strokeWidth={2} />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold">Since 2000</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Spotlight;
